import { HeaderButtons } from '@/features/invoice_reports/components/InvoiceReportDetailDrawer/components/HeaderButtons';
import type { ReportActionState } from '@/features/invoice_reports/types';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  IconInfo,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

type TitleInfo = {
  title: string;
  number: string;
};

type DrawerTitleProps = {
  titleInfo: TitleInfo;
  reportId: string;
  detailPath?: string;
  editPath?: string;
  editAction?: ReportActionState;
  deleteAction?: ReportActionState;
  cancelAction?: ReportActionState;
  shareAction?: ReportActionState;
  viewersCount?: number | null;
  isCopyable?: boolean;
  reportTotalAmount?: string | null;
  apPayeeName?: string | null;
  hasApprovalRoute?: boolean;
  onOpenApprovalRoute?: () => void;
  onOpenShareViewers?: () => void;
};

export const DrawerTitle: FC<DrawerTitleProps> = memo(
  ({
    titleInfo,
    reportId,
    detailPath,
    editPath,
    editAction,
    deleteAction,
    cancelAction,
    shareAction,
    viewersCount,
    isCopyable,
    reportTotalAmount,
    apPayeeName,
    hasApprovalRoute = false,
    onOpenApprovalRoute,
    onOpenShareViewers,
  }) => {
    const { t } = useTranslation();
    const container = useGlobalContainerRef();

    const approvalRouteButton = (
      <ButtonV2
        onClick={onOpenApprovalRoute}
        isTransparent
        isDisabled={!hasApprovalRoute}
        leftIcon={<IconInfo size={0} />}
      >
        {t('approving_invoice_report_detail_approval_route')}
      </ButtonV2>
    );

    return (
      <div className={cx('container')}>
        <div className={cx('header-leading-container')}>
          <div className={cx('info-container')}>
            <div className={cx('payment-request-number')}>
              <span className={cx('label')}>
                {t('approving_invoice_report_detail_number')}：
              </span>
              <span className={cx('value')}>{titleInfo.number}</span>
            </div>
            <div className={cx('payment-request-title')}>
              <span className={cx('label')}>
                {t('approving_invoice_report_detail_title')}：
              </span>
              <span className={cx('value')}>{titleInfo.title}</span>
              <div className={cx('approval-route-button')}>
                {hasApprovalRoute ? (
                  approvalRouteButton
                ) : (
                  <Tooltip
                    title={t('approval_route.button.disabled_tooltip')}
                    getPopupContainer={container}
                  >
                    <span>{approvalRouteButton}</span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
        <HeaderButtons
          reportId={reportId}
          detailPath={detailPath}
          editPath={editPath}
          editAction={editAction}
          deleteAction={deleteAction}
          cancelAction={cancelAction}
          shareAction={shareAction}
          viewersCount={viewersCount}
          isCopyable={isCopyable}
          onOpenShareViewers={onOpenShareViewers}
          reportNumber={titleInfo.number}
          reportTitle={titleInfo.title}
          reportTotalAmount={reportTotalAmount}
          apPayeeName={apPayeeName}
        />
      </div>
    );
  }
);

DrawerTitle.displayName = 'DrawerTitle';
